<script lang="ts" setup>
import { computed, inject, provide, ref } from 'vue'
import type { FieldGroupProvider, FieldProvider } from '../../types/field'
import { DTFieldGroupProvider, DTFieldInputWrapperProvider, DTFieldProvider } from '../../keys'
import type { FieldInputSizes } from './types'

const props = defineProps<{
  invalid?: boolean
  size?: keyof typeof FieldInputSizes
}>()

const labelProvider = inject<FieldProvider | undefined>(DTFieldProvider, undefined)
const groupProvider = inject<FieldGroupProvider | undefined>(DTFieldGroupProvider, undefined)

provide(DTFieldInputWrapperProvider, { size: props.size })

const sizeClass = computed<string>(() => {
  const size = props?.size ?? groupProvider?.size ?? labelProvider?.size
  switch (size) {
    case 'xs':
      return 'px-3 [&>.dt-field-input:not(:first-child)>input]:pl-1.5 [&>.dt-field-input:not(:last-child)>input]:pr-1.5'
    case 'sm':
      return 'px-3 [&>.dt-field-input:not(:first-child)>input]:pl-1.5 [&>.dt-field-input:not(:last-child)>input]:pr-1.5'
    case 'lg':
      return 'px-4.5 [&>.dt-field-input:not(:first-child)>input]:pl-4 [&>.dt-field-input:not(:last-child)>input]:pr-4'
    case 'md':
    default:
      return 'px-3.5 [&>.dt-field-input:not(:first-child)>input]:pl-2 [&>.dt-field-input:not(:last-child)>input]:pr-2'
  }
})

const touched = ref(false)
const groupClass = computed(() => {
  const cls = [
    'flex',
    'grow',
    'bg-white',
    'items-center',
    'border-solid',
    'border',
    'border-gray-300',
    'rounded-lg',
    '[&>.dt-field-input]:border-0',
    'focus-within:border-gray-800',
    '[&>.dt-field-input]:grow',
    '[&>.dt-field-input:first-child>input]:pl-0',
    '[&>.dt-field-input:last-child>input]:pr-0',
    'focus-within:z-10',
    'disabled:border-gray-300/40 disabled:text-gray-800/40',
  ]
  if (touched.value)
    cls.push('invalid:border-red-300', 'invalid:z-10')

  if (props.invalid)
    cls.push('border-red-300 focus-within:border-red-500 z-10')

  cls.push(sizeClass.value)

  return cls
})
</script>

<template>
  <fieldset
    :class="groupClass"
    @focusout="touched = true"
  >
    <slot />
  </fieldset>
</template>
